<script setup lang="ts">
import { inject } from 'vue';
import { forEach, map, sortBy } from 'lodash';
import CoursesService, { type RedirectUnitsListModel } from '../_runtime/services/CoursesService';
import UnitMaterialsService, { type ListItemModel as UnitMaterialListItemModel } from '../_runtime/services/UnitMaterialsService';
import SettingsService, { type PluginSettings } from '#layers/elearning/app/runtime/services/SettingsService';
import MaterialsService, { type FormModel, type GptContentCreatorFormModel } from '#layers/elearning/app/pages/elearning/modules/materials/_runtime/services/MaterialsService';
import MaterialCategoriesService, { type ListItemModel as CategoryItemModel, type FormModel as CategoryFormModel } from '#layers/elearning/app/pages/elearning/modules/materials/_runtime/services/MaterialCategoriesService';
import CategoriesMatchingSettings from '#layers/elearning/app/components/elearning/material-types/CategoriesMatchingSettings.vue';
import ContentMaterialSettings from '#layers/elearning/app/components/elearning/material-types/ContentSettings.vue';
import FillTheGapsSettings from '#layers/elearning/app/components/elearning/material-types/FillTheGapsSettings.vue';
import ImagesMatchingSettings from '#layers/elearning/app/components/elearning/material-types/ImagesMatchingSettings.vue';
import MatchingPairsSettings from '#layers/elearning/app/components/elearning/material-types/MatchingPairsSettings.vue';
import MultiSelectSettings from '#layers/elearning/app/components/elearning/material-types/MultiSelectSettings.vue';
import SelectListSettings from '#layers/elearning/app/components/elearning/material-types/SelectListSettings.vue';
import SetInOrderSettings from '#layers/elearning/app/components/elearning/material-types/SetInOrderSettings.vue';
import SingleSelectSettings from '#layers/elearning/app/components/elearning/material-types/SingleSelectSettings.vue';
import TrueFalseSettings from '#layers/elearning/app/components/elearning/material-types/TrueFalseSettings.vue';
// import VideoSettings from '@/modules/elearning/common/material-types/VideoSettings.vue';
// import OpenAiSettingsService from '@/modules/core/common/services/SettingsService';
// import OpenAiService, { type GptGeneratedMaterialModel } from '@/modules/core/common/services/OpenAiService';

defineOptions({
    components: {
        'type-categoriesmatching-settings': CategoriesMatchingSettings,
        'type-content-settings': ContentMaterialSettings,
        'type-fillthegaps-settings': FillTheGapsSettings,
        'type-imagesmatching-settings': ImagesMatchingSettings,
        'type-matchingpairs-settings': MatchingPairsSettings,
        'type-multiselect-settings': MultiSelectSettings,
        'type-selectlist-settings': SelectListSettings,
        'type-setinorder-settings': SetInOrderSettings,
        'type-singleselect-settings': SingleSelectSettings,
        'type-truefalse-settings': TrueFalseSettings
        // 'type-video-settings': VideoSettings
    }
});

// Props
interface Props
{
    modelValue: UnitMaterialListItemModel;
    filter: ModulesKey;
    unitOriginId?: number;
}

const props = withDefaults(defineProps<Props>(), {
    unitOriginId: 0
});

const { $t } = useLocalization();
const { $log } = useLogging();
const { $alert } = useAlerts();

// Emits
const emit = defineEmits<{
    materialUpdate: [updateEvent: UnitMaterialListItemModel];
    changesOnSlide: [value: string];
    changeForm: [form: FormModel];
}>();

// Inject values from parent
const providedCourseId = inject<number>('courseId');
const elementType = inject<string>('element_type');
const materialsChanged = inject<string>('materialsChanged');

// Refs
const type = ref<ValidatableComponent | null>(null);

// Reactive state
const fixedHeader = ref(false);
const pageTouched = ref(false);
const materialTypes = ref<PluginSettings[]>([]);
const categories = ref<CategoryItemModel[]>([]);
const redirectUnitOptions = ref<RedirectUnitsListModel[]>([]);
const redirectMaterialOptions = ref<UnitMaterialListItemModel[]>([]);
const redirectMaterialShow = ref(false);
const addingCategoryMode = ref(false);
const isGenerating = ref(false);
const tmpSlideId = ref<number>(0);
const slideId = ref<number>(0);

// const item = ref<GptGeneratedMaterialModel>({} as GptGeneratedMaterialModel);

const versions = ref<KeyValue<string, any>[]>([
    { key: '[[[Wybierz wersję]]]', value: null }
]);
const version = ref<KeyValue<string, any>>(versions.value[0]);

// Forms
const form = ref<FormType<FormModel>>(Form.create<FormModel>({
    id: 0,
    name: '',
    file: null,
    content: {
        value: null,
        type: 'None'
    },
    categoryId: 0,
    maxResult: 1,
    redirectUnitOriginId: null,
    redirectMaterialOriginId: null
}));

const categoryForm = reactive<FormType<CategoryFormModel>>(Form.create<CategoryFormModel>({
    id: 0,
    parentId: null,
    description: '',
    name: ''
}));

const gptContentCreatorForm = reactive<FormType<GptContentCreatorFormModel>>(Form.create<GptContentCreatorFormModel>({
    material: {
        id: 0,
        name: '',
        categoryName: '',
        contentType: ''
    },
    settings: {
        language: '',
        userRemarks: '',
        contentLength: 'medium',
        answerCount: null,
        questionCount: null,
        elementCompletedCount: null,
        categoryCount: null,
        itemCount: null
    }
}));

const openAiSettingsForm = reactive<FormType<Record<string, any>>>(Form.create<Record<string, any>>({
    isActive: false,
    isTestedAndCorrect: false,
    apiKey: null,
    modelVersion: null,
    maxTokens: null
}));

const permissions = ref<any>({
    canEdit: true
});

// Computed properties
const id = computed((): number =>
{
    return props.modelValue ? props.modelValue.materialOriginId : 0;
});

const unitId = computed((): number =>
{
    return props.modelValue ? props.modelValue.unitId : 0;
});

const contentLength = computed((): Option[] =>
{
    return [
        { value: 'short', text: $t('[[[Krótki tekst]]]') },
        { value: 'medium', text: $t('[[[Średni tekst]]]') },
        { value: 'long', text: $t('[[[Długi tekst]]]') }
    ];
});

const types = computed((): Option[] =>
{
    return [{ value: 'None', text: $t('[[[Brak typu zadania]]]') }].concat(materialTypes.value.map((p) =>
    {
        return { value: p.id, text: $t(p.name) };
    }));
});

const currentTypeComponent = computed((): string =>
{
    if (form.value.content.type)
    {
        const name = `type-${form.value.content.type.toLowerCase()}-settings`;

        return name;
    }

    return 'div';
});

const versionName = computed((): string =>
{
    return version.value.key;
});

// Methods
function check(): void
{
    const exist = localStorage.getItem(`no-save-${form.value.data().id}`);
    const existContent = localStorage.getItem(`content-no-save-${form.value.data().id}`);

    if (exist && existContent)
    {
        form.value.withData(JSON.parse(existContent));
    }
}

function checkElementTypeEqualEltest(): boolean
{
    if (elementType == 'el_tests')
        return true;

    return false;
}

async function initForm(): Promise<boolean>
{
    try
    {
        const response = await MaterialCategoriesService.getOptions();

        categories.value = [{
            id: 0,
            name: $t('[[[Brak kategorii]]]'),
            hasChildren: false
        }].concat(response);
    }
    catch (ex)
    {
        handleException($log, ex, {
            0: (ex: any) => form.value.$errors.push('parentId', $t('[[[Nie udało się pobrać listy kategorii]]]'))
        });

        return false;
    }

    try
    {
        materialTypes.value = await SettingsService.getConfig('material-types', props.filter);
    }
    catch (ex)
    {
        handleException($log, ex, {
            0: (ex: any) => form.value.$errors.push('parentId', $t('[[[Nie udało się pobrać listy typów materiałów]]]'))
        });

        return false;
    }

    return true;
}

function video(form: FormModel, save: boolean = false): FormModel
{
    if (form.content.type == 'Content')
    {
        const text = responsiveVideo(form.content.value.text, save);

        form.content.value.text = text;

        return form;
    }
    else
        return form;
}

async function loadData(): Promise<boolean>
{
    if (id.value == 0) return true;

    try
    {
        const response = await MaterialsService.fetch(id.value, props.unitOriginId);

        form.value.withData(response.result);
        permissions.value = response.meta;
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });

        return false;
    }

    return true;
}

async function loadRedirectUnits(): Promise<boolean>
{
    if (providedCourseId == 0 || elementType == null || !checkElementTypeEqualEltest()) return true;

    try
    {
        redirectUnitOptions.value = await CoursesService.getUnitsToRedirect(providedCourseId);
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });

        return false;
    }

    return true;
}

async function loadDataGeneratedMaterials(): Promise<boolean>
{
    if (id.value == 0) return true;

    try
    {
        // item.value = await OpenAiService.fetchGptGeneratedMaterial(0, id.value);

        initVersions();
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });

        return false;
    }

    return true;
}

async function onSubmit(forceOverride = false): Promise<void>
{
    try
    {
        form.value.wait();

        form.value.withData(video(form.value, true));

        if (!id.value)
        {
            const response = await MaterialsService.create(form.value.data(), props.unitOriginId);

            slideId.value = response.result.id;
            form.value.withData(video(response.result));
            $alert.success($t('[[[Slajd został dodany.]]]'));
        }
        else
        {
            if (!forceOverride && id.value > 0)
            {
                // zapisz nowa wersje slajdu, usun poprzednie wersje z local storage
                const exist = localStorage.getItem(`no-save-${form.value.data().id}`);
                const existContent = localStorage.getItem(`content-no-save-${form.value.data().id}`);

                exist && localStorage.removeItem(`no-save-${form.value.data().id}`);
                existContent && localStorage.removeItem(`content-no-save-${form.value.data().id}`);
            }

            const response = await MaterialsService.update(props.unitOriginId, id.value, form.value.data());

            form.value.withData(video(response.result));
            $alert.success($t('[[[Slajd został zaktualizowany.]]]'));
        }

        onMaterialUpdate();
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message),
            422: (ex: any) => form.value.$errors.record(ex.data.errors)
        });

        form.value.continue();
    }
}

async function onCategorySubmit(): Promise<void>
{
    try
    {
        categoryForm.wait();

        const response = await MaterialCategoriesService.create(categoryForm.data());

        $alert.success($t('[[[Kategoria została dodana.]]]'));
        categoryForm.withData(response.result);

        initForm();
        changeAddCategoryModeState();
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message),
            422: (ex: any) => categoryForm.$errors.record(ex.data.errors)
        });
    }
    finally
    {
        categoryForm.continue();
        form.value.continue();
    }
}

function onMaterialUpdate(): UnitMaterialListItemModel
{
    const updateEvent = {
        id: props.modelValue.id,
        unitId: unitId.value,
        materialId: form.value.id,
        materialOriginId: id.value ? id.value : form.value.id,
        position: props.modelValue.position,
        isPublished: props.modelValue.isPublished,
        type: form.value.content.type,
        name: form.value.name,
        maxResult: form.value.maxResult,
        redirectMaterialOriginId: form.value.redirectMaterialOriginId ?? null,
        redirectUnitOriginId: form.value.redirectUnitOriginId ?? null
    } as UnitMaterialListItemModel;

    emit('materialUpdate', updateEvent);

    return updateEvent;
}

async function changeAddCategoryModeState(): Promise<void>
{
    addingCategoryMode.value = !addingCategoryMode.value;
}

function checkSettingsCorrect(): boolean
{
    if (openAiSettingsForm.isTestedAndCorrect && openAiSettingsForm.isActive)
    {
        return true;
    }

    return false;
}

async function gptGenerateContent(): Promise<void>
{
    try
    {
        form.value.wait();

        isGenerating.value = true;

        // Przygotowujemy potrzebne informacje dla generatora treści
        MappingGptData();

        // Generujemy treść
        const responseGenerateContent = await MaterialsService.generateContent(gptContentCreatorForm.data());

        // this.form.value.withData(this.video(response.result));
        $alert.success($t('[[[Slajd został wygenerowany.]]]'));

        // Czyścimy wiadomości dotyczące błedów
        gptContentCreatorForm.$errors.clear();

        // Wstrzykujemy wygenerowane dane
        form.value.content.value = responseGenerateContent;
        initForm();

        if (!id.value)
        {
            // Zapisujemy wygenerowane dane
            await onSubmit();

            // Aktualizujemy SlideId w tabeli historii zapytań do API OpenAI
            // await OpenAiService.updateGptGeneratedMaterial(tmpSlideId.value, slideId.value);
        }

        // Pobieramy historię wygenerowanych danych dla danego slajdu
        await loadDataGeneratedMaterials();
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message),
            422: (ex: any) => gptContentCreatorForm.$errors.record(ex.data.errors)
        });
    }
    finally
    {
        form.value.continue();

        isGenerating.value = false;
    }
}

function MappingGptData(): void
{
    if (!id.value)
    {
        drawTmpSlideId();
    }

    gptContentCreatorForm.material.id = form.value.id != 0 ? form.value.id : tmpSlideId.value;
    gptContentCreatorForm.material.name = form.value.name;
    gptContentCreatorForm.material.categoryName = form.value.categoryId != 0 ? categories.value.find(x => x.id == form.value.categoryId)?.name : categoryForm.name;
    gptContentCreatorForm.material.contentType = form.value.content.type;
    gptContentCreatorForm.settings.language = 'pl';
}

function initVersions(): void
{
    // if (item.value.id != 0)
    // {
    //     versions.value = [{ key: '[[[Wybierz wersję]]]', value: null }];

    //     for (let index = 0; index < item.value.requestJsonObj.length; index++)
    //     {
    //         versions.value.push({ key: (index + 1).toString(), value: index });
    //     }
    // }
}

function getVersionData(item: any): void
{
    version.value = item;

    // if (item.value != null)
    // {
    //     const content = item.value.responseJsonObj[version.value.value].choices[0].message.content;

    //     form.value.content.value = getContent(content);
    // }
}

function getContent(content: string): any
{
    const cleanedString = content.replace(/[\n\r\t]/g, '');

    try
    {
        const contentObj = JSON.parse(cleanedString);

        return contentObj;
    }
    catch (error)
    {
        (window as any).$alert.warning($t('[[[Wybrana wersja slajdu została wygenerowana w nieprawidłowym formacie. Nie można jej wyświetlić.]]]'));

        return form.value.content.value;
    }
}

function getSlideTypeIdParameter(): string
{
    // return item.value.parametersJsonObj[version.value.value].slideTypeId;
    return '';
}

function showSettingsComponent(slideTypeIds: string[]): boolean
{
    if (checkSettingsCorrect() && slideTypeIds.includes(form.value.content.type))
    {
        return true;
    }

    return false;
}

// Losujemy tymczasowe id materiału dla generatora treści
function drawTmpSlideId(): void
{
    const min = 100_000;
    const max = 999_999;

    tmpSlideId.value = Math.floor(Math.random() * (max - min + 1) + min);
}

function changesOnSlide(value: string): string
{
    form.value.$errors.clear(value);

    changeForm();

    emit('changesOnSlide', value);

    return value;
}

function changeForm(): FormModel
{
    const formData = form.value.data();

    emit('changeForm', formData);

    return formData;
}

// Watchers
watch(() => form.value.content.type, (value: any) =>
{
    if (!id.value)
        form.value.content.value = null;

    form.value.$errors.clear();
});

watch(() => form.value.redirectUnitOriginId, async () =>
{
    redirectMaterialOptions.value = [];
    redirectMaterialShow.value = false;

    const oldRedirectMaterialOriginId = form.value.redirectMaterialOriginId;

    form.value.redirectMaterialOriginId = null;

    const redirectUnit = redirectUnitOptions.value.find(s => s.originId == form.value.redirectUnitOriginId);

    if (form.value.redirectUnitOriginId == null)
        return;

    if (redirectUnitOptions.value.length == 0)
        await loadRedirectUnits();

    if (redirectUnit?.module == 'el_scorm_lessons')
        return;

    const response = await UnitMaterialsService.getList(form.value.redirectUnitOriginId);

    redirectMaterialOptions.value = sortBy(map(response.items, m => m), m => m.position);
    forEach(redirectMaterialOptions.value, (el: any, i: number) => { el.position = i + 1; });

    if (redirectMaterialOptions.value.find(s => s.materialOriginId == oldRedirectMaterialOriginId))
        form.value.redirectMaterialOriginId = oldRedirectMaterialOriginId;

    redirectMaterialShow.value = true;
});

// Lifecycle hooks
onBeforeMount(async () =>
{
    // openAiSettingsForm.withData(await OpenAiSettingsService.fetch('openai'));

    await form.value.ready([
        initForm(),
        loadData(),
        loadDataGeneratedMaterials(),
        loadRedirectUnits()
    ]);

    check();
});
</script>

<template>
    <form @submit.prevent="onSubmit()" @input="changesOnSlide($event.target.name)">
        <PlaceholderForm :loading="form.loading()">
            <DataCard :header-responsive="true" class="material-board">
                <!-- Prawy panel -->
                <template #right>
                    <div v-if="checkSettingsCorrect()" class="alert alert-info">
                        {{ $t('[[[Pola takie jak "Nazwa slajdu", "Kategoria materiału", "Typ materiału" są uwzględniane przy automatycznym generowaniu treści, dlatego konieczne jest ich wypełnienie.]]]') }}
                    </div>
                    <IdeoFormGroup :label="$t('[[[Nazwa slajdu]]]')" :invalid-feedback="form.$errors.first('name')" :state="form.$errors.state('name')" required>
                        <IdeoFormInput v-model="form.name" v-focus type="text" name="name" />
                    </IdeoFormGroup>
                    <IdeoFormGroup :label="$t('[[[Kategoria materiału]]]')" :invalid-feedback="form.$errors.first('categoryId')" :state="form.$errors.state('categoryId')" required>
                        <IdeoInputGroup class="flex-nowrap">
                            <IdeoFormSelect v-model="form.categoryId" name="categoryId" :options="categories" value-field="id" text-field="name" />
                            <template #append>
                                <ActionButton :icon="addingCategoryMode ? 'fas fa-times' : 'fas fa-plus'" :title="$t('[[[Dodaj kategorię]]]')" @click="changeAddCategoryModeState()" />
                            </template>
                        </IdeoInputGroup>
                    </IdeoFormGroup>
                    <IdeoFormGroup
                        v-if="addingCategoryMode"
                        class="bg-light p-2 mb-3"
                        :label="$t('[[[Nazwa kategorii]]]')"
                        :invalid-feedback="categoryForm.$errors.first('name')"
                        :state="categoryForm.$errors.state('name')"
                        required
                    >
                        <IdeoInputGroup class="flex-nowrap">
                            <IdeoFormInput v-model="categoryForm.name" type="text" name="categoryName" />
                            <template #append>
                                <ActionButton variant="success" icon="fas fa-plus" :title="$t('[[[Dodaj kategorię]]]')" @click="onCategorySubmit()" />
                            </template>
                        </IdeoInputGroup>
                    </IdeoFormGroup>
                    <IdeoFormGroup :label="$t('[[[Typ materiału]]]')" :invalid-feedback="form.$errors.first('content.type')" :state="form.$errors.state('content.type')" required>
                        <IdeoFormSelect
                            v-model="form.content.type"
                            name="content.type"
                            :options="types"
                            value-field="value"
                            text-field="text"
                            :disabled="form.id > 0"
                        />
                    </IdeoFormGroup>
                    <IdeoFormGroup v-if="checkElementTypeEqualEltest()" :label="$t('[[[Lekcja do powtórzenia]]]')">
                        <IdeoFormSelect
                            v-model="form.redirectUnitOriginId"
                            name="redirectUnitOriginId"
                            :options="redirectUnitOptions"
                            value-field="originId"
                            text-field="name"
                            :disabled="!unitId || materialsChanged"
                        >
                            <template #first>
                                <option :value="null">
                                    {{ $t('[[[Brak]]]') }}
                                </option>
                            </template>
                        </IdeoFormSelect>
                        <template v-if="!unitId || materialsChanged" #description>
                            <span>{{ $t('[[[Wybór możliwy po zapisaniu zmian w teście.]]]') }}</span>
                        </template>
                    </IdeoFormGroup>
                    <IdeoFormGroup v-if="checkElementTypeEqualEltest() && redirectMaterialShow" :label="$t('[[[Slajd do powtórzenia]]]')">
                        <IdeoFormSelect
                            v-model="form.redirectMaterialOriginId"
                            name="redirectMaterialOriginId"
                            :options="redirectMaterialOptions"
                            value-field="materialOriginId"
                            text-field="name"
                            :disabled="!unitId || materialsChanged"
                        >
                            <template #first>
                                <option :value="null">
                                    {{ $t('[[[Brak]]]') }}
                                </option>
                            </template>
                        </IdeoFormSelect>
                        <template v-if="!unitId || materialsChanged" #description>
                            <span>{{ $t('[[[Wybór możliwy po zapisaniu zmian w teście.]]]') }}</span>
                        </template>
                    </IdeoFormGroup>
                    <IdeoFormGroup
                        v-if="form.content.type === 'Content'"
                        :label="$t('[[[Dodaj mp3]]]')"
                        :invalid-feedback="form.$errors.first('content.value.mp3File')"
                        :state="form.$errors.state('content.value.mp3File')"
                    >
                        <IdeoFilePicker v-model="form.content.value.mp3File" media="audio" :max-files="1" />
                    </IdeoFormGroup>
                    <IdeoFormGroup v-if="form.content.type === 'Content'">
                        <IdeoFormCheckbox v-model="form.content.value.mp3AutoStart" :invalid-feedback="form.$errors.first('content.value.mp3AutoStart')" :state="form.$errors.state('content.value.mp3AutoStart')">
                            {{ $t('[[[Uruchom nagranie automatycznie]]]') }}
                        </IdeoFormCheckbox>
                    </IdeoFormGroup>
                    <IdeoFormGroup v-if="form.content.type === 'Content'">
                        <IdeoFormCheckbox v-model="form.content.value.fullMp3ListeningRequired" :invalid-feedback="form.$errors.first('content.value.fullMp3ListeningRequired')" :state="form.$errors.state('content.value.fullMp3ListeningRequired')">
                            {{ $t('[[[Wymagaj pełnego odsłuchania do zaliczenia]]]') }}
                        </IdeoFormCheckbox>
                    </IdeoFormGroup>
                    <IdeoFormGroup
                        v-if="showSettingsComponent(['Content'])"
                        :label="$t('[[[Długość treści]]]')"
                        :invalid-feedback="gptContentCreatorForm.$errors.first('settings.contentLength')"
                        :state="gptContentCreatorForm.$errors.state('settings.contentLength')"
                    >
                        <IdeoFormSelect v-model="gptContentCreatorForm.settings.contentLength" name="contentLength" :options="contentLength" value-field="value" text-field="text" />
                        <template #description>
                            <span>{{ $t('[[[Wybierz długość generowanej treści.]]]') }}</span>
                        </template>
                    </IdeoFormGroup>
                    <IdeoFormGroup
                        v-if="showSettingsComponent(['SingleSelect', 'MultiSelect', 'SelectList', 'SetInOrder'])"
                        :label="$t('[[[Liczba odpowiedzi]]]')"
                        :invalid-feedback="gptContentCreatorForm.$errors.first('settings.answerCount')"
                        :state="gptContentCreatorForm.$errors.state('settings.answerCount')"
                    >
                        <input v-model="gptContentCreatorForm.settings.answerCount" type="number" class="form-control" :min="1" :max="10">
                    </IdeoFormGroup>
                    <IdeoFormGroup
                        v-if="showSettingsComponent(['TrueFalse', 'SelectList', 'MatchingPairs', 'ImagesMatching'])"
                        :label="$t('[[[Liczba pytań]]]')"
                        :invalid-feedback="gptContentCreatorForm.$errors.first('settings.questionCount')"
                        :state="gptContentCreatorForm.$errors.state('settings.questionCount')"
                    >
                        <input v-model="gptContentCreatorForm.settings.questionCount" type="number" class="form-control" :min="1" :max="10">
                    </IdeoFormGroup>
                    <IdeoFormGroup
                        v-if="showSettingsComponent(['FillTheGaps'])"
                        :label="$t('[[[Liczba elementów do uzupełnienia]]]')"
                        :invalid-feedback="gptContentCreatorForm.$errors.first('settings.elementCompletedCount')"
                        :state="gptContentCreatorForm.$errors.state('settings.elementCompletedCount')"
                    >
                        <input v-model="gptContentCreatorForm.settings.elementCompletedCount" type="number" class="form-control" :min="1" :max="10">
                    </IdeoFormGroup>
                    <IdeoFormGroup
                        v-if="showSettingsComponent(['CategoriesMatching'])"
                        :label="$t('[[[Liczba kategorii]]]')"
                        :invalid-feedback="gptContentCreatorForm.$errors.first('settings.categoryCount')"
                        :state="gptContentCreatorForm.$errors.state('settings.categoryCount')"
                    >
                        <input v-model="gptContentCreatorForm.settings.categoryCount" type="number" class="form-control" :min="1" :max="10">
                    </IdeoFormGroup>
                    <IdeoFormGroup
                        v-if="showSettingsComponent(['CategoriesMatching'])"
                        :label="$t('[[[Liczba elementów]]]')"
                        :invalid-feedback="gptContentCreatorForm.$errors.first('settings.itemCount')"
                        :state="gptContentCreatorForm.$errors.state('settings.itemCount')"
                    >
                        <input v-model="gptContentCreatorForm.settings.itemCount" type="number" class="form-control" :min="1" :max="10">
                    </IdeoFormGroup>
                    <!-- <IdeoFormGroup
                        v-if="checkSettingsCorrect()"
                        :label="$t('[[[Dodatkowe uwagi]]]')"
                        :invalid-feedback="gptContentCreatorForm.$errors.first('settings.userRemarks')"
                        :state="gptContentCreatorForm.$errors.state('settings.userRemarks')"
                    >
                        <IdeoFormTextarea v-model="gptContentCreatorForm.settings.userRemarks" name="userRemarks" />
                        <template #description>
                            <span>{{ $t('[[[Podaj uwagi do treści, która ma zostać wygenerowana. Przykład: "Używaj symboli matematycznych".]]]') }}</span>
                        </template>
                    </IdeoFormGroup> -->
                    <!-- <IdeoFormGroup v-if="item.id > 0 && form.content.type != 'ImagesMatching'">
                        <IdeoDropdown id="dropdown-left" :text="$t('[[[%0|||{0}]]]', $t(versionName))" class="mx-1 d-block" right>
                            <IdeoDropdownItemButton v-for="item in versions" :key="item.key" :class="{ 'text-primary': item === version }" @click="getVersionData(item)">
                                {{ $t('[[[%0|||{0}]]]', $t(item.key)) }}
                            </IdeoDropdownItemButton>
                        </IdeoDropdown>
                        <template #description>
                            <span>{{ $t('[[[Historia wygenerowanych treści.]]]') }}</span>
                        </template>
                    </IdeoFormGroup> -->
                    <ActionButton
                        v-if="checkSettingsCorrect()"
                        toolbar
                        variant="outline-dark mb-1 me-0"
                        :icon="isGenerating ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-brain-circuit'"
                        :text="$t('[[[Generuj treść slajdu]]]')"
                        :disabled="!form.active() || !permissions.canEdit"
                        @click="gptGenerateContent()"
                    />
                    <!-- <ideo-form-group :label="$t('[[[Punkty]]]')" :invalid-feedback="form.$errors.first('maxResult')" :state="form.$errors.state('maxResult')">
                        <ideo-form-input v-model="form.maxResult" type="number" name="maxResult" />
                    </ideo-form-group> -->
                    <div>
                        <ActionButton
                            toolbar
                            variant="primary mb-1 me-0"
                            icon="far fa-check"
                            :text="$t('[[[Zapisz slajd]]]')"
                            :disabled="!form.active() || !permissions.canEdit"
                            @click="onSubmit()"
                        />
                    </div>
                    <small v-if="form.modifiedBy" class="text=muted">
                        <span>{{ $t('[[[Ostatnio aktualizowane]]]') + ' ' + $filters.datetime(form.dateModifiedUtc) + ' ' + $t('[[[przez]]]') + ' ' + form.modifiedBy }}</span>
                    </small>
                </template>
                <!-- Dynamiczny component -->
                <div class="scroll">
                    <component :is="currentTypeComponent" ref="type" v-model="form" />
                </div>
            </DataCard>
        </PlaceholderForm>
    </form>
</template>

<style scoped lang="scss">
    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(-360deg);
        }
    }
    .loader {
        position: fixed;
        top: 50%;
        left: 50%;
        width: 200px;

        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        transform: translate(-50%, -50%);
        z-index: 9999;
    }
    .loader-color {
        padding: 10px;
        border: solid 1px #ebebeb;
        border-radius: 6px;
        backdrop-filter: blur(10px);
    }
    .loader-message {
        font-size: 20px;
        font-weight: 700;
        margin-top: 20px;
    }

    #chat-gpt-logo {
        animation: spin 4s linear infinite;
        transform-origin: center center;
    }

    .material-board {
        :deep(.panel.right) {
            padding: 1.25rem 1.875rem;
        }
    }

    .scroll {
        :deep(textarea) {
            flex: 1 1 0;
        }

        :deep(.p-inputtext)
        {
            flex: 1 1 0;
        }
    }
</style>
